'use client';

import { cn } from '@magicschool/spellbook/lib/utils';
import * as SeparatorPrimitive from '@radix-ui/react-separator';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

export type DividerProps = Omit<React.ComponentProps<typeof SeparatorPrimitive.Root>, 'asChild'> & VariantProps<typeof dividerVariants>;

export const dividerVariants = cva(['flex', 'shrink-0', 'data-[orientation=horizontal]:h-px', 'data-[orientation=vertical]:w-px'], {
  variants: {
    /** The visual appearance of the divider. */
    variant: {
      default: 'bg-border-default',
      strong: 'bg-border-strong',
    },
    /** Controls the height/width of the divider, depending on orientation. */
    full: {
      true: 'data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full',
      false: 'data-[orientation=horizontal]:w-auto data-[orientation=vertical]:h-auto',
    },
  },
});

/**
 * Visual separator built on Radix `Separator` that divides content sections horizontally or vertically.
 *
 * ### When to use
 * - To visually separate groups of related content (e.g., sections in a sidebar, form groups, toolbar button groups)
 * - As a vertical divider between inline elements like toolbar actions
 *
 * ### When NOT to use
 * - For spacing only with no visible line: use margin or gap utilities instead
 * - For bordered containers: use `Card` or a styled wrapper instead
 *
 * ### API
 * - `orientation`: `'horizontal'` (default) renders a full-width line, `'vertical'` renders a full-height line
 * - `variant`: `'default'` for standard border color, `'strong'` for emphasis. Note: `'muted'` has been removed — use `'default'` instead
 * - `full`: When `true` (default), the divider spans the full width or height of its container. Set to `false` for a partial-length divider
 * - `decorative`: When `true`, the divider is purely visual and hidden from screen readers. When `false` (default), it renders with `role="separator"`
 *
 * ### Accessibility
 * - Renders as a Radix `Separator` with `role="separator"` by default
 * - Set `decorative` to `true` when the divider is purely visual and does not represent a meaningful content boundary
 */
export const Divider: React.FC<DividerProps> = ({
  className,
  orientation = 'horizontal',
  decorative = false,
  full = true,
  variant = 'default',
  ...props
}) => {
  return (
    <SeparatorPrimitive.Root
      data-spellbook-name="divider"
      decorative={decorative}
      orientation={orientation}
      className={cn(dividerVariants({ variant, full }), className)}
      {...props}
    />
  );
};
